"use client";

import {
  Confirmation,
  ConfirmationAction,
  ConfirmationActions,
  ConfirmationAccepted,
  ConfirmationRejected,
  ConfirmationRequest,
  ConfirmationTitle,
} from "@/components/ai-elements/confirmation";
import type { ToolPart } from "@/components/ai-elements/tool";
import { BotIcon } from "@/components/ui/bot";
import {
  Avatar,
  AvatarFallback,
  AvatarGroup,
  AvatarGroupCount,
} from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ChevronDownIcon } from "@/components/ui/chevron-down";
import { ChevronRightIcon } from "@/components/ui/chevron-right";
import { FileTextIcon } from "@/components/ui/file-text";
import { FolderRootIcon } from "@/components/ui/folder-root";
import { Input } from "@/components/ui/input";
import { MessageCircleIcon } from "@/components/ui/message-circle";
import { Textarea } from "@/components/ui/textarea";
import { UsersIcon } from "@/components/ui/users";
import { appendReturnTo } from "@/lib/return-navigation";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import { isWoodsActionName, isWoodsWriteActionName, shouldShowWoodsAction } from "@/lib/woods-actions";
import { CalendarDaysIcon } from "lucide-react";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";

type RecordValue = Record<string, unknown>;

const ACTION_SURFACE_CLASS = "rounded-xl border border-border bg-card shadow-sm";
const ACTION_ERROR_SURFACE_CLASS = "rounded-xl border border-destructive/40 bg-destructive/5 shadow-sm";

const isRecord = (value: unknown): value is RecordValue =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

export function getWoodsActionName(part: ToolPart): string | null {
  const name = part.type === "dynamic-tool" ? part.toolName : part.type.replace(/^tool-/, "");
  return isWoodsActionName(name) ? name : null;
}

export const isWoodsActionToolPart = (part: ToolPart) => getWoodsActionName(part) !== null;

function text(value: unknown) {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .map((part) => part[0] ?? "")
    .join("")
    .slice(0, 2)
    .toLocaleUpperCase();
}

function actionDescription(name: string, input: RecordValue) {
  switch (name) {
    case "create_agent":
      return `create the agent “${text(input.name) ?? "Untitled agent"}”`;
    case "update_agent":
      return `update agent ${text(input.agent_id) ?? ""}`.trim();
    case "deactivate_current_agent":
      return "deactivate the current agent for this conversation";
    case "activate_agent":
      return `switch this conversation to agent ${text(input.agent_id) ?? ""}`;
    case "prepare_meeting":
      return `prepare the meeting “${text(input.title) ?? "Untitled meeting"}”`;
    case "save_chat_file_to_knowledge_base":
      return `save “${text(input.filename) ?? "this file"}” to the Library`;
    case "attach_file_to_project":
      return `add ${text(input.filename) ? `“${text(input.filename)}”` : "this file"} to the selected project`;
    case "request_file_team_transfer":
      return "request this file’s transfer to the selected team";
    case "move_current_chat_to_project":
      return "move this conversation to the selected project";
    default:
      return "perform this action";
  }
}

function InputSummary({ input }: { input: RecordValue }) {
  const rows = Object.entries(input).filter(([, value]) => value !== undefined && value !== null && value !== "");
  if (!rows.length) return null;
  return (
    <dl className="mt-3 grid gap-2 rounded-lg border border-border bg-muted/40 p-3 text-xs">
      {rows.map(([key, value]) => (
        <div className="grid min-w-0 grid-cols-[7rem_minmax(0,1fr)] gap-2" key={key}>
          <dt className="truncate font-medium text-muted-foreground">{key.replaceAll("_", " ")}</dt>
          <dd className="min-w-0 whitespace-pre-wrap break-words text-foreground">
            {Array.isArray(value) ? value.join(", ") : typeof value === "object" ? JSON.stringify(value) : String(value)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function ProjectMoveSummary({ projectId, shared }: { projectId: string | null; shared?: boolean }) {
  const [projectName, setProjectName] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    void supabase
      .from("projects")
      .select("name")
      .eq("id", projectId)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) setProjectName(data?.name ?? null);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  return (
    <dl className="mt-3 grid gap-2 rounded-lg border border-border bg-muted/40 p-3 text-xs">
      <div className="grid min-w-0 grid-cols-[7rem_minmax(0,1fr)] gap-2">
        <dt className="truncate font-medium text-muted-foreground">Project</dt>
        <dd className="min-w-0 truncate text-foreground">{projectName ?? "Selected project"}</dd>
      </div>
      {shared !== undefined ? (
        <div className="grid min-w-0 grid-cols-[7rem_minmax(0,1fr)] gap-2">
          <dt className="truncate font-medium text-muted-foreground">Access</dt>
          <dd className="min-w-0 text-foreground">{shared ? "Shared with project members" : "Private"}</dd>
        </div>
      ) : null}
    </dl>
  );
}

function AgentProposalForm({
  input,
  approvalId,
  onApprovalResponse,
}: {
  input: RecordValue;
  approvalId: string;
  onApprovalResponse: (params: { id: string; approved: boolean; reason?: string }) => void;
}) {
  const initialName = text(input.name) ?? "";
  const initialDescription = text(input.description) ?? "";
  const initialPrompt = text(input.prompt) ?? "";
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [prompt, setPrompt] = useState(initialPrompt);
  const changed = name.trim() !== initialName || description.trim() !== initialDescription || prompt.trim() !== initialPrompt;
  const canSubmit = Boolean(name.trim() && prompt.trim());
  const revisedInput = useMemo(() => ({ ...input, name: name.trim(), description: description.trim(), prompt: prompt.trim() }), [description, input, name, prompt]);

  return (
    <form
      className="mt-3 space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (!canSubmit) return;
        if (changed) {
          onApprovalResponse({
            id: approvalId,
            approved: false,
            reason: `The user revised the proposed agent. Call create_agent again with exactly this configuration so they can approve it: ${JSON.stringify(revisedInput)}`,
          });
          return;
        }
        onApprovalResponse({ id: approvalId, approved: true });
      }}
    >
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground" htmlFor={`${approvalId}-agent-name`}>Name</label>
        <Input id={`${approvalId}-agent-name`} maxLength={100} onChange={(event) => setName(event.target.value)} value={name} />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground" htmlFor={`${approvalId}-agent-description`}>Description</label>
        <Input id={`${approvalId}-agent-description`} maxLength={500} onChange={(event) => setDescription(event.target.value)} value={description} />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground" htmlFor={`${approvalId}-agent-prompt`}>Instructions</label>
        <Textarea className="min-h-28" id={`${approvalId}-agent-prompt`} maxLength={100000} onChange={(event) => setPrompt(event.target.value)} value={prompt} />
      </div>
      <InputSummary input={Object.fromEntries(Object.entries(input).filter(([key]) => !["name", "description", "prompt"].includes(key)))} />
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button onClick={() => onApprovalResponse({ id: approvalId, approved: false })} type="button" variant="outline">Cancel</Button>
        <Button disabled={!canSubmit} type="submit">{changed ? "Update proposal" : "Create agent"}</Button>
      </div>
    </form>
  );
}

function ResultCard({
  title,
  description,
  metadata,
  actions,
}: {
  title: string;
  description?: string | null;
  metadata?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className={cn(ACTION_SURFACE_CLASS, "px-4 py-3 text-card-foreground")}>
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-foreground">{title}</p>
        {description ? <p className="mt-1 line-clamp-3 text-xs text-muted-foreground">{description}</p> : null}
        {metadata ? <div className="mt-2 text-xs text-muted-foreground">{metadata}</div> : null}
      </div>
      {actions ? <div className="mt-3 flex flex-wrap gap-2">{actions}</div> : null}
    </div>
  );
}

function OpenButton({ href, children = "Open", compact = false, preserveChatReturn = true, showArrow = false, variant = "outline" }: { href?: string | null; children?: ReactNode; compact?: boolean; preserveChatReturn?: boolean; showArrow?: boolean; variant?: "default" | "outline" | "ghost" }) {
  const router = useRouter();
  if (!href) return null;
  const handleOpen = () => {
    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    router.push(preserveChatReturn ? appendReturnTo(href, currentPath) : href);
  };
  return (
    <Button className={compact ? "h-7 gap-1 px-2 text-xs" : undefined} onClick={handleOpen} size="sm" type="button" variant={variant}>
      {children}
      {showArrow ? <ChevronRightIcon aria-hidden="true" size={13} /> : null}
    </Button>
  );
}

function formatStatus(value: unknown) {
  const status = text(value);
  if (!status) return null;
  return status
    .replaceAll("_", " ")
    .toLocaleLowerCase()
    .replace(/(^|\s)\S/g, (character) => character.toLocaleUpperCase());
}

function StatusPill({ status }: { status?: string | null }) {
  if (!status) return null;
  const normalised = status.toLocaleLowerCase();
  return (
    <span
      className={cn(
        "hidden shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium sm:inline-flex",
        normalised.includes("complete")
          ? "bg-primary/10 text-primary"
          : normalised.includes("fail") || normalised.includes("error")
            ? "bg-destructive/10 text-destructive"
            : "bg-muted text-muted-foreground"
      )}
    >
      {status}
    </span>
  );
}

function CompactResultRow({
  title,
  details,
  icon,
  status,
  actions,
}: {
  title: string;
  details?: ReactNode;
  icon: ReactNode;
  status?: string | null;
  actions?: ReactNode;
}) {
  return (
    <div className="group flex min-w-0 items-center gap-3 px-3 py-2.5 transition-colors hover:bg-accent/40">
      <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{title}</p>
        {details ? <div className="truncate text-xs text-muted-foreground">{details}</div> : null}
      </div>
      <StatusPill status={status} />
      {actions ? <div className="flex shrink-0 items-center gap-0.5">{actions}</div> : null}
    </div>
  );
}

const COLLAPSED_RESULT_COUNT = 5;

function CompactResultList<T>({
  items,
  getKey,
  renderItem,
}: {
  items: T[];
  getKey: (item: T) => string;
  renderItem: (item: T) => ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);
  const visibleItems = expanded ? items : items.slice(0, COLLAPSED_RESULT_COUNT);
  const remaining = items.length - COLLAPSED_RESULT_COUNT;

  return (
    <div className={cn(ACTION_SURFACE_CLASS, "overflow-hidden text-card-foreground")}>
      <div className="divide-y divide-border">
        {visibleItems.map((item) => <div key={getKey(item)}>{renderItem(item)}</div>)}
      </div>
      {remaining > 0 ? (
        <Button
          className="h-8 w-full gap-1 rounded-none border-t border-border text-xs text-muted-foreground"
          onClick={() => setExpanded((current) => !current)}
          type="button"
          variant="ghost"
        >
          {expanded ? "Show less" : `Show ${remaining} more`}
          <ChevronDownIcon aria-hidden="true" className={cn("transition-transform", expanded && "rotate-180")} size={13} />
        </Button>
      ) : null}
    </div>
  );
}

function AgentCards({
  agents,
  onUseAgent,
}: {
  agents: RecordValue[];
  onUseAgent?: (agentId: string) => void;
}) {
  return (
    <CompactResultList
      getKey={(agent) => String(agent.id)}
      items={agents}
      renderItem={(agent) => {
        const agentId = text(agent.id);
        const teams = Array.isArray(agent.teams) ? agent.teams.filter(isRecord).map((team) => text(team.name)).filter(Boolean) : [];
        const tools = Array.isArray(agent.tools) ? agent.tools.filter(isRecord).map((entry) => text(entry.name) ?? text(entry.toolId)).filter(Boolean) : [];
        return (
          <CompactResultRow
            actions={
              <>
                <OpenButton compact href={text(agent.href)} variant="ghost">View</OpenButton>
                {agent.canEdit === true ? <OpenButton compact href={text(agent.editHref)} variant="ghost">Edit</OpenButton> : null}
                {agentId && onUseAgent ? (
                  <Button
                    className="h-7 gap-1 px-2 text-xs"
                    onClick={() => onUseAgent(agentId)}
                    size="sm"
                    type="button"
                    variant="outline"
                  >
                    Use
                    <ChevronRightIcon aria-hidden="true" className="size-3.5" />
                  </Button>
                ) : (
                  <OpenButton compact href={agentId ? `/?agent=${agentId}` : null} preserveChatReturn={false} showArrow variant="outline">Use</OpenButton>
                )}
              </>
            }
            details={[text(agent.description), teams.length ? `Team: ${teams.join(", ")}` : null, tools.length ? `Tools: ${tools.join(", ")}` : null].filter(Boolean).join(" · ")}
            icon={<BotIcon size={15} />}
            title={text(agent.name) ?? "Agent"}
          />
        );
      }}
    />
  );
}

function MeetingCards({ meetings }: { meetings: RecordValue[] }) {
  return (
    <CompactResultList
      getKey={(meeting) => String(meeting.id)}
      items={meetings}
      renderItem={(meeting) => {
        const attendees = Array.isArray(meeting.attendees)
          ? meeting.attendees
              .filter(isRecord)
              .map((person, index) => ({
                id: text(person.user_id) ?? `${text(person.name) ?? "attendee"}-${index}`,
                name: text(person.name),
              }))
              .filter((person): person is { id: string; name: string } => Boolean(person.name))
          : [];
        const createdAt = text(meeting.createdAt);
        const date = createdAt ? new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(createdAt)) : null;
        const status = formatStatus(meeting.status);
        return (
          <CompactResultRow
            actions={<OpenButton compact href={text(meeting.href)} showArrow variant="ghost">Open</OpenButton>}
            details={
              <div className="flex min-w-0 items-center gap-2">
                {date ? <span className="shrink-0">{date}</span> : null}
                {date && attendees.length ? <span aria-hidden="true">·</span> : null}
                {attendees.length ? (
                  <>
                    <AvatarGroup aria-label={`Attendees: ${attendees.map((person) => person.name).join(", ")}`} className="shrink-0">
                      {attendees.slice(0, 3).map((person) => (
                        <Avatar aria-label={person.name} data-slot="avatar" key={person.id} size="sm" title={person.name}>
                          <AvatarFallback className="bg-foreground text-background text-[10px] font-medium">
                            {initials(person.name)}
                          </AvatarFallback>
                        </Avatar>
                      ))}
                      {attendees.length > 3 ? (
                        <AvatarGroupCount className="text-[10px]" title={attendees.slice(3).map((person) => person.name).join(", ")}>
                          +{attendees.length - 3}
                        </AvatarGroupCount>
                      ) : null}
                    </AvatarGroup>
                    <span className="min-w-0 truncate">{attendees.map((person) => person.name).join(", ")}</span>
                  </>
                ) : null}
              </div>
            }
            icon={<CalendarDaysIcon aria-hidden="true" className="size-4" />}
            status={status}
            title={text(meeting.title) ?? "Meeting"}
          />
        );
      }}
    />
  );
}

function ResourceCards({ items, kind }: { items: RecordValue[]; kind: "project" | "team" | "file" }) {
  const icon = kind === "project"
    ? <FolderRootIcon size={15} />
    : kind === "team"
      ? <UsersIcon size={15} />
      : <FileTextIcon size={15} />;
  return (
    <CompactResultList
      getKey={(item) => String(item.id)}
      items={items}
      renderItem={(item) => (
        <CompactResultRow
          actions={<OpenButton compact href={text(item.href)} showArrow variant="ghost">Open</OpenButton>}
          details={[kind === "project" ? text(item.project_number) : null, text(item.description)].filter(Boolean).join(" · ")}
          icon={icon}
          status={kind === "file" ? formatStatus(item.processing_status) : null}
          title={text(item.name) ?? kind}
        />
      )}
    />
  );
}

function SourceCards({ items, kind }: { items: RecordValue[]; kind: "project" | "chat" }) {
  return (
    <CompactResultList
      getKey={(item) => String(item.id)}
      items={items}
      renderItem={(item) => {
        const content = text(item.content) ?? "";
        const snippet = content.length > 160 ? `${content.slice(0, 160)}…` : content;
        const title = kind === "project" ? text(item.projectName) ?? "Project" : text(item.chatName) ?? "Chat";
        return (
          <CompactResultRow
            actions={
              <OpenButton compact href={text(item.href)} showArrow variant="ghost">
                {kind === "project" ? "Go to project" : "Go to chat"}
              </OpenButton>
            }
            details={snippet}
            icon={kind === "project" ? <FolderRootIcon size={15} /> : <MessageCircleIcon size={15} />}
            title={title}
          />
        );
      }}
    />
  );
}

function ActionOutput({
  name,
  output,
  onUseAgent,
}: {
  name: string;
  output: RecordValue;
  onUseAgent?: (agentId: string) => void;
}) {
  if (text(output.error)) {
    return <div className={cn(ACTION_ERROR_SURFACE_CLASS, "px-4 py-3 text-sm text-destructive")}>{text(output.error)}</div>;
  }
  if (Array.isArray(output.agents)) {
    const agents = output.agents.filter(isRecord);
    return agents.length ? <AgentCards agents={agents} onUseAgent={onUseAgent} /> : null;
  }
  if (isRecord(output.agent) && name === "get_agent_details") return <AgentCards agents={[output.agent]} onUseAgent={onUseAgent} />;
  if (Array.isArray(output.meetings)) {
    const meetings = output.meetings.filter(isRecord);
    return meetings.length ? <MeetingCards meetings={meetings} /> : <ResultCard title={text(output.message) ?? "No matching meetings found."} />;
  }
  if (Array.isArray(output.projects)) {
    const projects = output.projects.filter(isRecord);
    return projects.length ? <ResourceCards items={projects} kind="project" /> : <ResultCard title={text(output.message) ?? "No projects found."} />;
  }
  if (Array.isArray(output.teams)) {
    const teams = output.teams.filter(isRecord);
    return teams.length ? <ResourceCards items={teams} kind="team" /> : <ResultCard title={text(output.message) ?? "No teams found."} />;
  }
  if (Array.isArray(output.files)) {
    const files = output.files.filter(isRecord);
    return files.length ? <ResourceCards items={files} kind="file" /> : <ResultCard title={text(output.message) ?? "No Library files found."} />;
  }
  if (Array.isArray(output.sources) && (name === "search_my_projects" || name === "search_my_chats")) {
    const sources = output.sources.filter(isRecord);
    return sources.length
      ? <SourceCards items={sources} kind={name === "search_my_projects" ? "project" : "chat"} />
      : null;
  }

  const entity = [output.agent, output.meeting, output.file, output.project].find(isRecord);
  const href = (entity && text(entity.href)) ?? text(output.href) ?? text(output.route) ?? (isRecord(output.project) ? text(output.project.href) : null);
  const title =
    text(output.message) ??
    (name === "create_agent" ? "Agent created." :
      name === "update_agent" ? "Agent updated." :
      name === "deactivate_current_agent" ? "Agent deactivated." :
      name === "prepare_meeting" ? "Meeting prepared." :
      name === "save_chat_file_to_knowledge_base" ? "File saved to the Library." :
      name === "attach_file_to_project" ? "File added to the project." :
      name === "request_file_team_transfer" ? "Team transfer requested." :
      name === "move_current_chat_to_project" ? "Conversation moved to the project." :
      text(output.title) ?? "Action completed.");
  const description = text(output.summary) ?? (output.indexing === "queued" ? "Library processing has been queued." : null);

  return (
    <ResultCard
      actions={href ? <OpenButton href={href} variant="default">Open</OpenButton> : undefined}
      description={description}
      metadata={output.requiresUserStart === true ? "Microphone access starts only after you press Start recording." : undefined}
      title={title}
    />
  );
}

export function WoodsActionCard({
  toolPart,
  onApprovalResponse,
  onUseAgent,
  onAgentDeactivated,
}: {
  toolPart: ToolPart;
  onApprovalResponse: (params: { id: string; approved: boolean; reason?: string }) => void;
  onUseAgent?: (agentId: string) => void;
  onAgentDeactivated?: () => void;
}) {
  const name = getWoodsActionName(toolPart);
  const deactivationNotifiedRef = useRef(false);
  useEffect(() => {
    if (
      name === "deactivate_current_agent" &&
      toolPart.state === "output-available" &&
      isRecord(toolPart.output) &&
      toolPart.output.deactivated === true &&
      !deactivationNotifiedRef.current
    ) {
      deactivationNotifiedRef.current = true;
      onAgentDeactivated?.();
    }
  }, [name, onAgentDeactivated, toolPart.output, toolPart.state]);

  if (!name || !shouldShowWoodsAction(name, toolPart.input)) return null;
  const input = isRecord(toolPart.input) ? toolPart.input : {};

  if (isWoodsWriteActionName(name) && ["approval-requested", "approval-responded", "output-denied"].includes(toolPart.state)) {
    const description = actionDescription(name, input);
    if (name === "create_agent" && toolPart.state === "approval-requested" && toolPart.approval && !toolPart.approval.isAutomatic) {
      return (
        <Confirmation className={ACTION_SURFACE_CLASS} approval={toolPart.approval} state={toolPart.state}>
          <ConfirmationTitle>
            <ConfirmationRequest>Review the proposed agent before it is created.</ConfirmationRequest>
            <ConfirmationAccepted>Agent creation approved.</ConfirmationAccepted>
            <ConfirmationRejected>Agent creation cancelled or revised.</ConfirmationRejected>
          </ConfirmationTitle>
          <AgentProposalForm input={input} approvalId={toolPart.approval.id} onApprovalResponse={onApprovalResponse} />
        </Confirmation>
      );
    }
    return (
      <Confirmation className={ACTION_SURFACE_CLASS} approval={toolPart.approval} state={toolPart.state}>
        <ConfirmationTitle>
          <ConfirmationRequest>Woods AI wants to {description}. Review the details and allow this?</ConfirmationRequest>
          <ConfirmationAccepted>Approved - Woods AI will {description}.</ConfirmationAccepted>
          <ConfirmationRejected>Denied - Woods AI will not {description}.</ConfirmationRejected>
        </ConfirmationTitle>
        {toolPart.state === "approval-requested" ? (
          name === "move_current_chat_to_project" ? (
            <ProjectMoveSummary
              projectId={text(input.project_id)}
              shared={typeof input.shared === "boolean" ? input.shared : undefined}
            />
          ) : (
            <InputSummary input={input} />
          )
        ) : null}
        <ConfirmationActions>
          <ConfirmationAction
            onClick={() => toolPart.approval && onApprovalResponse({ id: toolPart.approval.id, approved: false })}
            variant="outline"
          >
            Cancel
          </ConfirmationAction>
          <ConfirmationAction
            onClick={() => toolPart.approval && onApprovalResponse({ id: toolPart.approval.id, approved: true })}
          >
            Confirm
          </ConfirmationAction>
        </ConfirmationActions>
      </Confirmation>
    );
  }

  if (toolPart.state === "output-available" && isRecord(toolPart.output)) {
    return <ActionOutput name={name} output={toolPart.output} onUseAgent={onUseAgent} />;
  }

  if (toolPart.state === "output-error") {
    return <div className={cn(ACTION_ERROR_SURFACE_CLASS, "px-4 py-3 text-sm text-destructive")}>{toolPart.errorText || "The action failed."}</div>;
  }

  return null;
}
